/**
 * FAQ accordion — progressive enhancement for the c-faq disclosure component
 * (assets/views/components/c-faq.twig, styled by assets/scss/c-faq.scss).
 *
 * The markup is a native <details>/<summary> list, so it already expands and
 * collapses, exposes a disclosure to assistive tech, and is keyboard operable
 * with NO JavaScript. This script layers on top:
 *
 *   - single-open behaviour (opening one item closes the others),
 *   - aria-expanded kept in sync on each <summary>,
 *   - animated open AND close, driven by an `.is-open` class so the CSS
 *     grid-rows transition reverses smoothly when an item is clicked again
 *     mid-animation (no rAF/inline-style juggling, no stutter),
 *   - prefers-reduced-motion honoured: open/close is instant.
 *
 * State model: `.is-open` (class) drives the visual animation; the native
 * `open` attribute is added before opening (so the content renders) and removed
 * only once the collapse transition has finished (so the content stays in flow
 * while it animates closed, then is hidden). Because the animation keys off the
 * class, rapid clicks just retarget the in-flight transition.
 *
 * `js-` hooks (js-faq, js-faq-item, js-faq-summary, js-faq-answer-wrap) carry
 * behaviour only and are never styled.
 */
(function () {
  "use strict";

  var SINGLE_OPEN = true;

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function initRoot(root) {
    var items = Array.prototype.slice.call(
      root.querySelectorAll(".js-faq-item")
    );
    if (!items.length) {
      return;
    }

    // Flag the enhanced state so the CSS opts into the animated reveal.
    root.classList.add("c-faq--js");

    items.forEach(function (item) {
      var summary = item.querySelector(".js-faq-summary");
      if (!summary) {
        return;
      }

      // Mirror any server-rendered default-open item into our class state.
      if (item.open) {
        item.classList.add("is-open");
      }
      syncAria(item, summary);

      // When the collapse transition ends and the item is still closed, drop
      // the native [open] so the content leaves the flow (and AT tree).
      var wrap = item.querySelector(".js-faq-answer-wrap");
      if (wrap) {
        wrap.addEventListener("transitionend", function (event) {
          if (
            event.target === wrap &&
            event.propertyName === "grid-template-rows" &&
            !item.classList.contains("is-open")
          ) {
            item.open = false;
          }
        });
      }

      // Intercept activation so we control open/close. The browser dispatches a
      // click on <summary> for both mouse and keyboard (Enter/Space).
      summary.addEventListener("click", function (event) {
        event.preventDefault();
        toggleItem(items, item, summary);
      });
    });
  }

  function syncAria(item, summary) {
    summary.setAttribute("aria-expanded", item.open ? "true" : "false");
  }

  function toggleItem(items, item, summary) {
    if (item.classList.contains("is-open")) {
      closeItem(item, summary);
      return;
    }

    if (SINGLE_OPEN) {
      items.forEach(function (other) {
        if (other !== item && other.classList.contains("is-open")) {
          closeItem(other, other.querySelector(".js-faq-summary"));
        }
      });
    }

    openItem(item, summary);
  }

  function openItem(item, summary) {
    // Render the content first, then flip the class so 0fr → 1fr transitions
    // (a brand-new [open] starts collapsed, then animates open).
    item.open = true;
    if (summary) {
      summary.setAttribute("aria-expanded", "true");
    }

    if (prefersReducedMotion()) {
      item.classList.add("is-open");
      return;
    }

    // Two frames, not one: the first rAF still runs before the style/layout
    // pass that gives the just-opened content its 0fr start value, so adding
    // the class there would collapse both states into one recalc and the
    // browser would jump straight to 1fr. The second rAF lands after that
    // pass, so there is a real previous value to transition from.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        item.classList.add("is-open");
      });
    });
  }

  function closeItem(item, summary) {
    item.classList.remove("is-open");
    if (summary) {
      summary.setAttribute("aria-expanded", "false");
    }

    // Reduced motion (or no transition): hide immediately, no transitionend.
    if (prefersReducedMotion()) {
      item.open = false;
    }
  }

  function init() {
    var roots = document.querySelectorAll(".js-faq");
    Array.prototype.forEach.call(roots, initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
